import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Plane,
  Hotel,
  Car,
  Ticket,
  Utensils,
  MapPin,
  ArrowRight,
  Cloud,
  Droplets,
  Check,
  X,
  Mail,
} from "lucide-react";
import { TeachingBanner } from "../components/TeachingBanner";
import { Breadcrumb } from "../components/Breadcrumb";
import { TripClaimedFactsPanel } from "../components/ClaimedFactsPanel";
import { useOpenPacket } from "../components/PacketViewerContext";
import { useAttention, useViewEmission, type AttentionViewItem } from "../lib/useAttention";

/** E2 Travel: the multi-leg Trip dossier. Renders the legs timeline
 *  (with travel arrows), weather forecast, AI summary, flights + stays,
 *  and source emails (open in the Q42 packet viewer). */
interface Trip {
  id: string;
  title: string;
  destination: string | null;
  origin: string | null;
  start_at: string | null;
  end_at: string | null;
  segment_count?: number;
  booking_count?: number;
  provider?: string | null;
  source_packet_ids?: string | null;
  meta_json?: string | null;
  confirmed?: number;
}
interface Segment {
  id: string;
  mode?: string | null;
  carrier: string | null;
  origin: string | null;
  destination: string | null;
  start_at: string | null;
  end_at: string | null;
  confirmation: string | null;
  provider: string | null;
}
interface Booking {
  id: string;
  kind: string;
  brand: string | null;
  location: string | null;
  start_at: string | null;
  end_at: string | null;
  confirmation: string | null;
  provider: string | null;
}
interface Leg {
  city: string | null;
  start_at: string | null;
  end_at: string | null;
  provider?: string | null;
}
interface WeatherDay {
  date: string;
  high_c: number | null;
  low_c: number | null;
  precip_mm: number | null;
  label: string;
}
interface Weather {
  resolved_name: string | null;
  country: string | null;
  days: WeatherDay[];
}
interface Summary {
  text: string;
  source: string;
}

function fmtDay(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

const BOOKING_ICON: Record<string, typeof Hotel> = {
  hotel: Hotel,
  car: Car,
  ticket: Ticket,
  restaurant: Utensils,
};

export function TripDetail() {
  const { id } = useParams<{ id: string }>();
  const [trip, setTrip] = useState<Trip | null>(null);
  const [segments, setSegments] = useState<Segment[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const openPacket = useOpenPacket();
  const { emit } = useAttention("trip");

  const viewItems = useMemo<AttentionViewItem[]>(() => {
    if (!trip) return [];
    const items: AttentionViewItem[] = [
      { cluster: trip.id, itemKind: "trip", itemId: trip.id, context: { confirmed: !!trip.confirmed } },
    ];
    for (const s of segments) {
      items.push({ cluster: trip.id, itemKind: "trip_segment", itemId: s.id, context: { mode: s.mode ?? null, carrier: s.carrier } });
    }
    for (const b of bookings) {
      items.push({ cluster: trip.id, itemKind: "trip_booking", itemId: b.id, context: { kind: b.kind, brand: b.brand } });
    }
    return items;
  }, [trip, segments, bookings]);
  useViewEmission("trip", viewItems);

  const load = useCallback(() => {
    if (!id) return;
    fetch(`/api/agenda/trips/${encodeURIComponent(id)}`, {
      credentials: "same-origin",
    })
      .then((r) =>
        r.ok
          ? r.json()
          : Promise.reject(
              new Error(r.status === 404 ? "not found" : `HTTP ${r.status}`),
            ),
      )
      .then((j) => {
        setTrip(j.trip);
        setSegments(j.segments ?? []);
        setBookings(j.bookings ?? []);
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const act = useCallback(
    async (verb: "confirm" | "reject") => {
      if (!id) return;
      setBusy(true);
      void emit(verb === "confirm" ? "accept" : "dismiss", {
        cluster: id,
        itemKind: "trip",
        itemId: id,
      });
      try {
        await fetch(`/api/agenda/trips/${encodeURIComponent(id)}/${verb}`, {
          method: "POST",
          credentials: "same-origin",
        });
        load();
      } finally {
        setBusy(false);
      }
    },
    [id, load, emit],
  );

  if (error)
    return (
      <div className="w-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 py-6 space-y-4">
        <Breadcrumb
          segments={[{ label: "Agenda", href: "/agenda" }, { label: "Trips" }]}
        />
        <div className="rounded-lg border border-rose-300 dark:border-rose-700 bg-rose-50 dark:bg-rose-900/30 px-4 py-3 text-sm text-rose-800 dark:text-rose-200">
          Could not load this trip: {error}
        </div>
      </div>
    );
  if (!trip)
    return (
      <div className="w-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 py-10 text-sm text-slate-400">
        Loading…
      </div>
    );

  let meta: Record<string, unknown> = {};
  try {
    meta = JSON.parse(trip.meta_json || "{}");
  } catch {
    meta = {};
  }
  const legs: Leg[] = Array.isArray(meta.legs) ? (meta.legs as Leg[]) : [];
  const cities: string[] = Array.isArray(meta.cities)
    ? (meta.cities as string[])
    : [];
  const weather = (meta.weather as Weather | undefined) ?? null;
  const summary = (meta.summary as Summary | undefined) ?? null;
  const packetIds: string[] = (() => {
    try {
      const v = JSON.parse(trip.source_packet_ids || "[]");
      return Array.isArray(v) ? v : [];
    } catch {
      return [];
    }
  })();
  const inferred = !trip.confirmed;

  return (
    <div className="w-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 py-6 space-y-5">
      <Breadcrumb
        segments={[
          { label: "Agenda", href: "/agenda" },
          { label: "Trips" },
          { label: trip.title },
        ]}
      />

      <header className="flex items-start gap-4 flex-wrap">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-sky-400 to-cyan-500 flex items-center justify-center text-white shadow-md flex-shrink-0">
          <Plane className="w-6 h-6" />
        </div>
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
            {trip.title}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
            {fmtDay(trip.start_at)}
            {trip.end_at && trip.end_at !== trip.start_at
              ? ` to ${fmtDay(trip.end_at)}`
              : ""}
            {cities.length > 1 ? ` · ${cities.length} cities` : ""}
          </p>
          {inferred && (
            <span className="inline-block mt-1.5 text-[11px] px-2 py-0.5 rounded-full border border-dashed border-slate-400 dark:border-slate-600 text-slate-500 dark:text-slate-400">
              Inferred from travel signals
            </span>
          )}
        </div>
        {inferred && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => act("confirm")}
              disabled={busy}
              className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50"
            >
              <Check className="w-4 h-4" /> Confirm
            </button>
            <button
              onClick={() => act("reject")}
              disabled={busy}
              className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-50"
            >
              <X className="w-4 h-4" /> Not a trip
            </button>
          </div>
        )}
      </header>

      <TeachingBanner id="trip_detail_overview" />

      <TripClaimedFactsPanel tripId={trip.id} title="What Memso knows about this trip" />

      {/* Legs timeline with travel arrows */}
      {legs.length > 0 && (
        <section className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-800/40 p-5">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-3">
            Route
          </h2>
          <div className="flex items-center gap-2 flex-wrap">
            {legs.map((leg, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className="flex flex-col items-center px-3 py-2 rounded-xl bg-slate-100 dark:bg-slate-700/50">
                  <span className="text-sm font-medium text-slate-800 dark:text-slate-100">
                    {leg.city ?? "Unknown"}
                  </span>
                  <span className="text-[11px] text-slate-400">
                    {fmtDay(leg.start_at)}
                  </span>
                </div>
                {i < legs.length - 1 && (
                  <ArrowRight className="w-4 h-4 text-sky-400 flex-shrink-0" />
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* AI summary dossier */}
      {summary?.text && (
        <section className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-800/40 p-5">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">
            Summary
          </h2>
          <div className="text-sm text-slate-700 dark:text-slate-200 space-y-1 whitespace-pre-line">
            {summary.text}
          </div>
        </section>
      )}

      {/* Weather forecast */}
      {weather && weather.days.length > 0 && (
        <section className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-800/40 p-5">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-3 flex items-center gap-1.5">
            <Cloud className="w-3.5 h-3.5" /> Weather
            {weather.resolved_name ? ` · ${weather.resolved_name}` : ""}
          </h2>
          <div className="flex gap-3 flex-wrap">
            {weather.days.map((d) => (
              <div
                key={d.date}
                className="flex flex-col items-center px-3 py-2 rounded-xl bg-slate-100 dark:bg-slate-700/50 min-w-[84px]"
              >
                <span className="text-[11px] text-slate-400">
                  {fmtDay(d.date)}
                </span>
                <span className="text-sm font-medium text-slate-800 dark:text-slate-100">
                  {d.high_c != null ? `${Math.round(d.high_c)}°` : "--"}
                  <span className="text-slate-400 text-xs">
                    {d.low_c != null ? ` / ${Math.round(d.low_c)}°` : ""}
                  </span>
                </span>
                <span className="text-[11px] text-slate-500 dark:text-slate-400 text-center">
                  {d.label}
                </span>
                {d.precip_mm != null && d.precip_mm > 0 && (
                  <span className="text-[10px] text-sky-500 flex items-center gap-0.5">
                    <Droplets className="w-2.5 h-2.5" />
                    {d.precip_mm}mm
                  </span>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Flights */}
      {segments.length > 0 && (
        <section className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-800/40 p-5">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-3 flex items-center gap-1.5">
            <Plane className="w-3.5 h-3.5" /> Flights
          </h2>
          <div className="space-y-2">
            {segments.map((s) => (
              <div
                key={s.id}
                className="flex items-center justify-between gap-3 text-sm text-slate-700 dark:text-slate-200"
              >
                <span className="flex items-center gap-2">
                  {[s.carrier, [s.origin, s.destination].filter(Boolean).join(" to ")]
                    .filter(Boolean)
                    .join(" · ") || "Flight"}
                </span>
                <span className="text-xs text-slate-400">
                  {fmtDay(s.start_at)}
                  {s.confirmation ? ` · ${s.confirmation}` : ""}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Stays + other bookings */}
      {bookings.length > 0 && (
        <section className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-800/40 p-5">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-3 flex items-center gap-1.5">
            <Hotel className="w-3.5 h-3.5" /> Stays &amp; bookings
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {bookings.map((b) => {
              const Icon = BOOKING_ICON[b.kind] ?? MapPin;
              return (
                <div
                  key={b.id}
                  className="flex items-start gap-3 px-3 py-2.5 rounded-xl bg-slate-100 dark:bg-slate-700/50"
                >
                  <Icon className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">
                      {b.brand || b.location || b.kind}
                    </div>
                    <div className="text-[11px] text-slate-400">
                      {[b.location, fmtDay(b.start_at)].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Source emails -> Q42 packet viewer */}
      {packetIds.length > 0 && (
        <section className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-800/40 p-5">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-3 flex items-center gap-1.5">
            <Mail className="w-3.5 h-3.5" /> Source emails ({packetIds.length})
          </h2>
          <div className="flex flex-wrap gap-2">
            {packetIds.map((pid) => (
              <button
                key={pid}
                onClick={() => openPacket(pid)}
                className="text-xs px-2.5 py-1 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                Open source
              </button>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
